import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useContext, useRef, useState } from "react";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useDynamicMenuHeight } from "../../hooks/useDynamicMenuHeight";
import { useUniqueId } from "../../hooks/useUniqueId";
import { Popper } from "../../popper/Popper";
import { FieldsetContext } from "../fieldset/Fieldset";
import { SelectMenu } from "./SelectMenu";
import { OptionListHandler } from "./OptionListHandler";
import { SelectToggle } from "./SelectToggle";
import { SelectOption } from "./SelectOption";
import { SelectOptionCheckbox } from "./SelectOptionCheckbox";
import { SelectMessageOption } from "./SelectMessageOption";
import { SelectLoadingOption } from "./SelectLoadingOption";
import { SelectBackOption } from "./SelectBackOption";
import { SelectSearchFilter } from "./SelectSearchFilter";
import * as Styled from "./Select.styled";
import { findValue, getSelectMenuWidthStyle } from "./utils";
import { ROUGH_ITEM_HEIGHT } from "./constants";
export const SelectComponent = forwardRef((props, ref) => {
    const { backgroundVariant, children, defaultValue, disabled: disabledProp, endAdornmentWidth, hasStatus, id, isFullWidth, isLoading, isSearchable, labelId, loadingProps, menuMaxHeight: menuMaxHeightProp, menuMaxWidth, name, noOptionsMessage, noSearchResultsMessage, onBlur, onChange, onFocus, onMenuOpen, onMenuClose, onMenuScroll, onMenuScrollEnd, onSearchFilter, options, overlayZIndex, palette, popperProps, placeholder, renderMenu = renderDefaultMenu, renderToggle = renderDefaultToggle, renderSearchInput, renderSelectedOption, required, searchProps, shouldAutoFocusMenu, shouldClearAncestorsOnSearch, startAdornmentWidth, stringMap: stringMapProp, toggleProps, value, ...otherProps } = props;
    const fieldsetOptions = useContext(FieldsetContext);
    const idOrFallback = useUniqueId("select-", id);
    const wrapperRef = useRef(null);
    const toggleRef = useRef(null);
    const disabled = disabledProp || fieldsetOptions.disabled;
    const { menuMaxHeight, placement, fallbackPlacements } = useDynamicMenuHeight({
        toggleRef,
        offset: isSearchable ? ROUGH_ITEM_HEIGHT : 0,
        isFullWidth
    });
    const [selectedOption, setSelectedOption] = useState(defaultValue === undefined ? undefined : findValue(defaultValue, options));
    const updateSelectValue = (closeAndReturnFocus) => (option, callback) => {
        if (value === undefined) {
            setSelectedOption(option);
        }
        closeAndReturnFocus && closeAndReturnFocus();
        onChange && onChange(option);
        callback && callback(option);
    };
    const getMenuContent = ({ closeAndReturnFocus, isOpen: _isOpen, overlayRef, setIsOpen }) => (_jsx(Styled.MenuDisplayBox, { depth: "menu", borderRadius: "extraLarge", hoverDepth: "menu", style: getSelectMenuWidthStyle({
            isFullWidth,
            parentWidth: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().width : undefined,
            menuMaxWidth
        }), children: renderMenu({
            // TODO: deprecate labeling with select toggle contents (value), require an external label instead, otherwise render empty to force a11y failures
            "aria-labelledby": labelId ?? idOrFallback,
            children,
            closeAndReturnFocus,
            isFullWidth,
            isLoading,
            isSearchable,
            keyboardDirection: "vertical",
            loadingProps,
            menuWidth: isFullWidth && wrapperRef.current ? wrapperRef.current.getBoundingClientRect().width : undefined,
            noOptionsMessage,
            noSearchResultsMessage,
            onMenuOpen,
            onMenuClose,
            onMenuScroll,
            onMenuScrollEnd,
            onSearchFilter,
            onUpdate: updateSelectValue(closeAndReturnFocus),
            options,
            // this is marked as optional but Popper actually provides it at all times
            overlayRef,
            maxHeight: menuMaxHeightProp || menuMaxHeight,
            renderSearchInput,
            searchProps,
            selectedValue: value,
            selectedOption,
            shouldAutoFocusMenu,
            shouldClearAncestorsOnSearch,
            stringMap: stringMapProp,
            // @ts-expect-error -- add for backwards compat but don't show in TS
            setIsOpen,
            toggleRef
        }) }));
    const handleKeyDown = (setIsOpen) => (event) => {
        const preferredKeys = ["ArrowDown", "ArrowUp", "Enter", " "];
        if (preferredKeys.includes(event.key)) {
            event.preventDefault();
            // Timeout works around bug on IE11 where opening the dropdown causes the page to scroll down.
            setTimeout(() => {
                setIsOpen(true);
            }, 0);
        }
    };
    return (_jsx(Styled.Wrapper, { ref: useCombineRefs(ref, wrapperRef), isFullWidth: isFullWidth, "data-testid": "Select", ...otherProps, children: _jsx(Popper, { mode: "popover", overlay: getMenuContent, placement: placement, fallbackPlacements: fallbackPlacements, zIndex: overlayZIndex, shouldCloseOnOutsideInteraction: true, ...popperProps, trigger: ({ isOpen, setIsOpen }) => renderToggle({
                "aria-expanded": isOpen,
                backgroundVariant,
                startAdornmentWidth,
                endAdornmentWidth,
                ...toggleProps,
                isFullWidth,
                isOpen,
                onBlur,
                onFocus,
                onKeyDown: handleKeyDown(setIsOpen),
                placeholder,
                hasStatus,
                disabled,
                options,
                palette,
                renderSelectedOption,
                required,
                id: idOrFallback,
                labelId,
                selectedValue: value,
                selectedOption,
                toggleRef
            }) }) }));
});
const Select = Object.assign(SelectComponent, {
    Toggle: SelectToggle,
    Menu: SelectMenu,
    Option: SelectOption,
    OptionCheckbox: SelectOptionCheckbox,
    MessageOption: SelectMessageOption,
    LoadingOption: SelectLoadingOption,
    BackOption: SelectBackOption,
    SearchFilter: SelectSearchFilter
});
const renderDefaultToggle = (toggleProps) => {
    const { toggleRef, ...otherProps } = toggleProps;
    return _jsx(SelectToggle, { ref: toggleRef, ...otherProps });
};
export const renderDefaultMenu = (menuProps) => (_jsx(SelectMenu, { ...menuProps, children: menuProps.children ? menuProps.children : (childProps) => _jsx(OptionListHandler, { ...menuProps, ...childProps }) }));
Select.displayName = "Select";
Select.defaultProps = {
    backgroundVariant: "container",
    colorScheme: "alchemyPanel",
    popperProps: {},
    noOptionsMessage: "No Options Available",
    noSearchResultsMessage: "No Results Found",
    palette: "interactiveConstructive",
    renderMenu: renderDefaultMenu,
    renderToggle: renderDefaultToggle,
    shouldAutoFocusMenu: true
};
export { Select };
